import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Button,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { io } from "socket.io-client";

import EmailPanelIzquierdo from "./EmailPanelIzquierdo";
import EmailPanelDerecho from "./EmailPanelDerecho";
import AttachmentsLibrary from "./AttachmentsLibrary";

import EmailEnviarPro from "../eNuevo/EmailEnviarPro";
import EmailContestar from "../eContestar/EmailContestar";
import EmailReenviar from "../eReenviar/EmailReenviar";

import AccountManager from "../../cuentas/AccountManager";

import DeleteIcon from "@mui/icons-material/Delete";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import { useUniboxColors } from "../../useUniboxColors";
import {
  buildGroupedThreads,
  LIST_GROUPING_STORAGE_KEY,
  readListGrouping,
} from "./threadGrouping";
import ToolbarIconButton from "./ToolbarIconButton";

const API = process.env.REACT_APP_API_URL;

function SelectionLayoutToggle({ mode, onChange, vertical = false }) {
  const c = useUniboxColors();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        gap: 0.4,
        ...(vertical
          ? { pt: 0.75, mt: 0.25, borderTop: `1px solid ${c.border}` }
          : { pl: 1, ml: 0.5, borderLeft: `1px solid ${c.border}` }),
      }}
    >
      {[
        { id: "top", label: "A" },
        { id: "side", label: "D" },
      ].map(({ id, label }) => (
        <IconButton
          key={id}
          size="small"
          onClick={() => onChange(id)}
          sx={{
            width: 28,
            height: 28,
            fontSize: 11,
            fontWeight: 800,
            borderRadius: c.radius,
            bgcolor: mode === id ? c.accent : "transparent",
            color: mode === id ? "#fff" : c.textMuted,
            border: `1px solid ${mode === id ? c.accent : c.border}`,
            "&:hover": {
              bgcolor: mode === id ? c.selectedBorder : c.cardHover,
            },
          }}
        >
          {label}
        </IconButton>
      ))}
    </Box>
  );
}

function SelectionActions({
  onDelete,
  onRead,
  onUnread,
  onClear,
  onSelectAll,
  showSelectAll,
  selectAllLabel,
  vertical = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "center",
        gap: 0.65,
      }}
    >
      {showSelectAll && (
        <ToolbarIconButton
          title={selectAllLabel}
          onClick={onSelectAll}
          icon={<SelectAllIcon sx={{ fontSize: "1.05rem" }} />}
        />
      )}
      <ToolbarIconButton
        title="Eliminar"
        onClick={onDelete}
        variant="danger"
        icon={<DeleteIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Marcar como leído"
        onClick={onRead}
        variant="primary"
        icon={<DoneIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Marcar como no leído"
        onClick={onUnread}
        icon={<MailOutlineIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Cancelar selección"
        onClick={onClear}
        icon={<CloseIcon sx={{ fontSize: "1.05rem" }} />}
      />
    </Box>
  );
}

export default function EmailGeneral() {
  const [threads, setThreads] = useState({});
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [openAccounts, setOpenAccounts] = useState(false);

  const [showSendPanel, setShowSendPanel] = useState(false);

  const [showReplyPanel, setShowReplyPanel] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const [showForwardPanel, setShowForwardPanel] = useState(false);
  const [forwardEmail, setForwardEmail] = useState(null);
  const [forwardHtml, setForwardHtml] = useState("");
  const [forwardOriginalAttachments, setForwardOriginalAttachments] = useState(
    []
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [authError, setAuthError] = useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = useState(true);

  const modoVista = "email";

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [listGrouping, setListGrouping] = useState(readListGrouping);
  const [folder, setFolder] = useState("inbox");

  const [attachmentsByEmail, setAttachmentsByEmail] = useState({});
  const [bodyCache, setBodyCache] = useState({});

  const [selectedEmails, setSelectedEmails] = useState([]); // selección múltiple
  const [selectionMode, setSelectionMode] = useState(false); // si hay o no selección

  const [selectionBarMode, setSelectionBarMode] = useState("top");

  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] =
    useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const c = useUniboxColors();
  const [mobileView, setMobileView] = useState("list");
  const [attachmentsLibraryOpen, setAttachmentsLibraryOpen] = useState(false);
  const [focusGmailId, setFocusGmailId] = useState(null);

  const selectConversation = (key) => {
    if (key !== selectedPerson) {
      setSelectedEmails([]);
      setSelectionMode(false);
    }
    setSelectedPerson(key);
    if (isMobile) setMobileView("thread");
  };

  const handleMobileBack = () => {
    setMobileView("list");
  };

  useEffect(() => {
    if (!isMobile) setMobileView("list");
  }, [isMobile]);

  /* Al cambiar de conversación o filtro: vaciar selección (evita borrar emails de otro hilo) */
  useEffect(() => {
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [selectedPerson, filter]);

  const threadsRef = useRef({});
  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  const folderRef = useRef(folder);
  useEffect(() => {
    folderRef.current = folder;
  }, [folder]);

  const bodyCacheRef = useRef({});
  useEffect(() => {
    bodyCacheRef.current = bodyCache;
  }, [bodyCache]);

  const attachmentsByEmailRef = useRef({});
  useEffect(() => {
    attachmentsByEmailRef.current = attachmentsByEmail;
  }, [attachmentsByEmail]);

  /* ---------------- LOAD ACCOUNTS (Sync del menú usa este estado) ---------------- */
  const reloadAccounts = useCallback(() => {
    axios
      .get(`${API}/auth/accounts`)
      .then((res) => {
        setAccounts(res.data);
        setAuthError(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAuthError(true);
          return;
        }
        console.error("Error cargando cuentas:", err);
      });
  }, []);

  useEffect(() => {
    reloadAccounts();
  }, [reloadAccounts]);

  /* ---------------- LOAD EMAILS ---------------- */
  const loadEmails = useCallback(async () => {
    const activeFolder = folderRef.current;
    const alreadyHaveThreads =
      Object.keys(threadsRef.current || {}).length > 0;
    if (!alreadyHaveThreads) {
      setIsLoadingEmails(true);
    }
    try {
      const res = await axios.get(`${API}/emails-from-db`, {
        params: { folder: activeFolder, _ts: Date.now() },
      });
      const data = res.data;

      const summary = await axios.get(`${API}/emails/attachments-summary`);
      const hasAttachmentsMap = summary.data;

      // Marca emails con adjuntos
      for (const person in data) {
        data[person] = data[person].map((email) => ({
          ...email,
          hasAttachments: (hasAttachmentsMap[email.gmailId] || 0) > 0,
        }));
      }

      setThreads(data);
      setAuthError(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthError(true);
        return;
      }
      console.error("Error cargando emails:", err);
    } finally {
      setIsLoadingEmails(false);
    }
  }, [API]);

  const conversationThreads = React.useMemo(
    () => buildGroupedThreads(threads, listGrouping),
    [threads, listGrouping]
  );

  const findThreadKeyForGmailId = useCallback(
    (gmailId) => {
      if (!gmailId) return null;
      for (const [key, emails] of Object.entries(conversationThreads || {})) {
        if ((emails || []).some((e) => e.gmailId === gmailId)) return key;
      }
      return null;
    },
    [conversationThreads]
  );

  const openAttachmentInConversation = useCallback(
    (item) => {
      const gmailId = item?.gmailId;
      if (!gmailId) return;

      const threadKey = findThreadKeyForGmailId(gmailId);
      if (!threadKey) {
        window.alert(
          "No se encontró la conversación de este archivo. Prueba a sincronizar la cuenta."
        );
        return;
      }

      setAttachmentsLibraryOpen(false);
      if (folder === "trash") setFolder("inbox");
      selectConversation(threadKey);
      setFocusGmailId(gmailId);
    },
    [findThreadKeyForGmailId, folder]
  );

  const visibleThreadEmailIds = useMemo(() => {
    if (!selectedPerson) return [];
    const raw = conversationThreads[selectedPerson];
    let list = Array.isArray(raw) ? [...raw] : [];
    if (filter === "read") list = list.filter((e) => e.isRead);
    if (filter === "unread") list = list.filter((e) => !e.isRead);
    return list.map((e) => e.gmailId).filter(Boolean);
  }, [conversationThreads, selectedPerson, filter]);

  const selectedInVisibleThread = useMemo(() => {
    const allowed = new Set(visibleThreadEmailIds);
    return selectedEmails.filter((id) => allowed.has(id));
  }, [selectedEmails, visibleThreadEmailIds]);

  const allInThreadSelected =
    visibleThreadEmailIds.length > 0 &&
    visibleThreadEmailIds.every((id) => selectedInVisibleThread.includes(id));

  useEffect(() => {
    const keys = Object.keys(conversationThreads || {});
    if (keys.length === 0) {
      if (selectedPerson) setSelectedPerson(null);
      return;
    }

    if (selectedPerson && conversationThreads[selectedPerson]) return;

    setSelectedPerson(keys[0]);
  }, [conversationThreads, selectedPerson]);

  const listGroupingInitialized = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(LIST_GROUPING_STORAGE_KEY, listGrouping);
    } catch {
      /* ignore */
    }

    if (!listGroupingInitialized.current) {
      listGroupingInitialized.current = true;
      return;
    }

    setSelectedPerson(null);
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [listGrouping]);

  useEffect(() => {
    loadEmails();
  }, [folder, loadEmails]);

  useEffect(() => {
    if (folder === "trash" && filter !== "all") {
      setFilter("all");
    }
    setSelectedPerson(null);
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [folder]);

  /* ---------------- SOCKET SYNC ---------------- */
  useEffect(() => {
    const socket = io(API);
    socket.on("emails-updated", loadEmails);
    return () => socket.disconnect();
  }, [API, loadEmails]);

  /* ---------------- LOAD BODY ---------------- */
  const loadBodyCached = useCallback(async (gmailId) => {
    if (bodyCacheRef.current[gmailId]) return bodyCacheRef.current[gmailId];

    try {
      const res = await axios.get(`${API}/emails/${gmailId}/body`);
      const html = res.data.html || res.data.text || "";

      setBodyCache((prev) => ({ ...prev, [gmailId]: html }));
      return html;
    } catch (err) {
      console.error(err);
      setBodyCache((prev) => ({ ...prev, [gmailId]: "" }));
      return "";
    }
  }, [API]);

  /* ---------------- LOAD ATTACHMENTS ---------------- */
  const attachmentsInflightRef = useRef(new Set());
  const loadAttachments = useCallback(async (gmailId) => {
    if (attachmentsByEmailRef.current[gmailId] !== undefined) return;
    if (attachmentsInflightRef.current.has(gmailId)) return;
    attachmentsInflightRef.current.add(gmailId);
    try {
      const res = await axios.get(`${API}/emails/${gmailId}/attachments`);
      const files = res.data;

      setAttachmentsByEmail((prev) => ({ ...prev, [gmailId]: files }));
    } catch (err) {
      console.error(err);
      setAttachmentsByEmail((prev) => ({ ...prev, [gmailId]: [] }));
    } finally {
      attachmentsInflightRef.current.delete(gmailId);
    }
  }, [API]);

  /* ---------------- TOGGLE READ ---------------- */
  const toggleRead = async (gmailId) => {
    const res = await axios.post(`${API}/emails/${gmailId}/toggle-read`);
    const newState = res.data.isRead;

    setThreads((prev) => {
      const updated = { ...prev };
      for (const p in updated) {
        updated[p] = updated[p].map((e) =>
          e.gmailId === gmailId ? { ...e, isRead: newState } : e
        );
      }
      return updated;
    });
  };

  /* ---------------- DELETE ---------------- */
  const borrar = async () => {
    if (folder === "trash") {
      await axios.delete(`${API}/emails/${deleteId}/permanent`);
    } else {
      await axios.post(`${API}/emails/${deleteId}/trash`);
    }

    setThreads((prev) => {
      const updated = { ...prev };
      for (const p in updated) {
        updated[p] = updated[p].filter((e) => e.gmailId !== deleteId);
      }
      return updated;
    });

    setShowDeleteDialog(false);
  };

  /* ---------------- OPEN FORWARD ---------------- */
  const openForward = async (email) => {
    setForwardEmail(email);

    const res = await axios.get(
      `${API}/emails/${email.gmailId}/attachments/full`
    );
    const originalFiles = res.data;

    const body = await loadBodyCached(email.gmailId);

    const forwardedBlock = `
      <br/>
      ---------- Mensaje reenviado ----------<br/>
      <b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/>
      <b>Para:</b> ${email.to}<br/><br/>
      ${body}
    `;

    setForwardHtml(forwardedBlock);
    setForwardOriginalAttachments(originalFiles);
    setShowForwardPanel(true);
  };

  function stringToPastelColor(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  }

  function getAccountColor(email) {
    const account = accounts.find((a) => a.id === email.account_id);
    if (!account) return "#e2e8f0"; // gris claro

    return stringToPastelColor(account.email);
  }

  const toggleSelectEmail = (gmailId) => {
    setSelectedEmails((prev) => {
      if (prev.includes(gmailId)) {
        const updated = prev.filter((id) => id !== gmailId);
        if (updated.length === 0) setSelectionMode(false);
        return updated;
      } else {
        setSelectionMode(true);
        return [...prev, gmailId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedEmails([]);
    setSelectionMode(false);
  };

  const toggleSelectAllInThread = useCallback(() => {
    if (allInThreadSelected) {
      clearSelection();
      return;
    }
    if (visibleThreadEmailIds.length === 0) return;
    setSelectedEmails(visibleThreadEmailIds);
    setSelectionMode(true);
  }, [allInThreadSelected, visibleThreadEmailIds]);

  const confirmDeleteSelected = async () => {
    try {
      const ids = selectedInVisibleThread;
      if (!ids.length) {
        clearSelection();
        setShowDeleteSelectedDialog(false);
        return;
      }

      const deleteOne =
        folder === "trash"
          ? (id) => axios.delete(`${API}/emails/${id}/permanent`).catch(() => null)
          : (id) => axios.post(`${API}/emails/${id}/trash`).catch(() => null);

      await Promise.all(ids.map((id) => deleteOne(id)));

      clearSelection();
      loadEmails();
      setShowDeleteSelectedDialog(false);
    } catch (err) {
      console.error("Error al eliminar emails:", err);
    }
  };

  const deleteConversationThreads = async (threadKeys) => {
    const keys = Array.isArray(threadKeys) ? threadKeys : [];
    if (!keys.length) return;

    const ids = keys.flatMap((key) =>
      (conversationThreads[key] || []).map((e) => e.gmailId).filter(Boolean)
    );
    if (!ids.length) return;

    const deleteOne =
      folder === "trash"
        ? (id) => axios.delete(`${API}/emails/${id}/permanent`).catch(() => null)
        : (id) => axios.post(`${API}/emails/${id}/trash`).catch(() => null);

    await Promise.all(ids.map((id) => deleteOne(id)));

    if (keys.includes(selectedPerson)) {
      setSelectedPerson(null);
    }
    clearSelection();
    await loadEmails();
  };

  const markSelectedAsRead = async () => {
    for (const id of selectedInVisibleThread) {
      // 1) Pido el estado actual
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      // 2) Si YA está leído → no hago nada
      if (email?.isRead) continue;

      // 3) Sino → toggle
      await axios.post(`${API}/emails/${id}/toggle-read`);
    }

    clearSelection();
    loadEmails();
  };

  const markSelectedAsUnread = async () => {
    for (const id of selectedInVisibleThread) {
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      if (!email || !email.isRead) continue;

      await axios.post(`${API}/emails/${id}/toggle-read`);
    }

    clearSelection();
    loadEmails();
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: { xs: "100dvh", md: "100vh" },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {authError ? (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff4e5",
            color: "#7a2e0b",
            border: "1px solid #ffd8a8",
            borderRadius: "8px",
            px: 2,
            py: 1,
            zIndex: 9999,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Clave de acceso no valida. Cierra sesion y vuelve a entrar.
        </Box>
      ) : null}

      {/* PANEL IZQUIERDO */}
      <Box
        sx={{
          display: {
            xs: isMobile && mobileView === "thread" ? "none" : "flex",
            md: "flex",
          },
          flexShrink: 0,
          width: { xs: "100%", md: "auto" },
          minWidth: 0,
          minHeight: 0,
        }}
      >
      <EmailPanelIzquierdo
        threads={conversationThreads}
        selectedPerson={selectedPerson}
        setSelectedPerson={selectConversation}
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
        folder={folder}
        setFolder={setFolder}
        setOpenAccounts={setOpenAccounts}
        setShowSendPanel={setShowSendPanel} // 🔥 AÑADIDO
        accounts={accounts} // 🔥 AÑADIDO
        onEmptyTrash={async () => {
          await axios.delete(`${API}/emails/trash/permanent`);
          await loadEmails();
        }}
        onDeleteConversations={deleteConversationThreads}
        isMobile={isMobile}
        listGrouping={listGrouping}
        setListGrouping={setListGrouping}
        onOpenAttachmentsLibrary={() => setAttachmentsLibraryOpen(true)}
      />
      </Box>

      {selectionMode && selectedInVisibleThread.length > 0 && (
        <>
          {selectionBarMode === "top" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: { xs: 0, md: 340 },
                right: 0,
                height: 52,
                bgcolor: c.overlayBg,
                backdropFilter: "blur(10px)",
                borderBottom: `1px solid ${c.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: { xs: 1.5, md: 2 },
                gap: 1.5,
                zIndex: 30,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: c.textPrimary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedInVisibleThread.length} seleccionado
                  {selectedInVisibleThread.length === 1 ? "" : "s"}
                </Typography>
                {visibleThreadEmailIds.length > 1 && !isMobile && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SelectAllIcon sx={{ fontSize: 16 }} />}
                    onClick={toggleSelectAllInThread}
                    sx={{
                      textTransform: "none",
                      fontSize: 11,
                      py: 0.35,
                      px: 1.25,
                      borderRadius: c.radius,
                      borderColor: c.border,
                      color: c.textPrimary,
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: c.accentBorder,
                        bgcolor: c.accentSoft,
                      },
                    }}
                  >
                    {allInThreadSelected ? "Quitar todos" : "Todos"}
                  </Button>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                <SelectionActions
                  onDelete={() => setShowDeleteSelectedDialog(true)}
                  onRead={markSelectedAsRead}
                  onUnread={markSelectedAsUnread}
                  onClear={clearSelection}
                />
                <SelectionLayoutToggle
                  mode={selectionBarMode}
                  onChange={setSelectionBarMode}
                />
              </Box>
            </Box>
          )}

          {selectionBarMode === "side" && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: 16,
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                bgcolor: c.overlayBg,
                backdropFilter: "blur(10px)",
                p: 0.85,
                borderRadius: c.radiusOuter,
                border: `1px solid ${c.border}`,
                boxShadow: c.shadowMd,
                zIndex: 40,
              }}
            >
              <SelectionActions
                onDelete={() => setShowDeleteSelectedDialog(true)}
                onRead={markSelectedAsRead}
                onUnread={markSelectedAsUnread}
                onClear={clearSelection}
                onSelectAll={toggleSelectAllInThread}
                showSelectAll={visibleThreadEmailIds.length > 1 && !isMobile}
                selectAllLabel={allInThreadSelected ? "Quitar todos" : "Todos"}
                vertical
              />
              <SelectionLayoutToggle
                mode={selectionBarMode}
                onChange={setSelectionBarMode}
                vertical
              />
            </Box>
          )}
        </>
      )}

      {/* PANEL DERECHO */}
      {isLoadingEmails ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            bgcolor: c.panelAlt,
          }}
        >
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 13, color: c.textMuted, fontWeight: 600 }}>
            Cargando conversaciones...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: {
              xs: isMobile && mobileView === "list" ? "none" : "flex",
              md: "flex",
            },
            flex: 1,
            minWidth: 0,
            minHeight: 0,
          }}
        >
        <EmailPanelDerecho
          threads={conversationThreads}
          selectedPerson={selectedPerson}
          filter={filter}
          folder={folder}
          modoVista={modoVista}
          bodyCache={bodyCache}
          attachmentsByEmail={attachmentsByEmail}
          loadAttachments={loadAttachments}
          loadBodyCached={loadBodyCached}
          toggleRead={toggleRead}
          openReply={(email) => {
            setSelectedEmail(email);
            setShowReplyPanel(true);
          }}
          openForward={openForward}
          askDelete={(id) => {
            setDeleteId(id);
            setShowDeleteDialog(true);
          }}
          restoreEmail={async (id) => {
            await axios.post(`${API}/emails/${id}/restore`);
            await loadEmails();
          }}
          getAccountColor={getAccountColor}
          selectionMode={selectionMode}
          selectedEmails={selectedInVisibleThread}
          toggleSelectEmail={toggleSelectEmail}
          enterSelectionMode={() => setSelectionMode(true)}
          clearSelection={clearSelection}
          selectionBarMode={selectionBarMode}
          isMobile={isMobile}
          onBack={handleMobileBack}
          listGrouping={listGrouping}
          focusGmailId={focusGmailId}
          onFocusGmailIdClear={() => setFocusGmailId(null)}
        />
        </Box>
      )}

      {/* SEND NEW EMAIL */}
      <EmailEnviarPro
        open={showSendPanel}
        onClose={() => setShowSendPanel(false)}
        accounts={accounts}
        onSent={loadEmails}
      />

      {/* RESPONDER */}
      <EmailContestar
        open={showReplyPanel}
        onClose={() => setShowReplyPanel(false)}
        email={selectedEmail}
        accounts={accounts}
        originalHtml={bodyCache[selectedEmail?.gmailId] || ""}
        onSend={async (form) => {
          try {
            await axios.post(`${API}/emails/send`, form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            loadEmails().catch((err) =>
              console.error("Error recargando emails tras responder:", err)
            );
          } catch (err) {
            console.error("Error enviando respuesta:", err);
          } finally {
            setShowReplyPanel(false);
            setSelectedEmail(null);
          }
        }}
      />

      {/* REENVIAR */}
      <EmailReenviar
        open={showForwardPanel}
        onClose={() => setShowForwardPanel(false)}
        fromEmail={accounts[0]?.email}
        email={forwardEmail}
        forwardedHtml={forwardHtml}
        forwardOriginalAttachments={forwardOriginalAttachments}
        onSend={async (form) => {
          await axios.post(`${API}/emails/send`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          loadEmails();
          setShowForwardPanel(false);
        }}
      />

      {/* DELETE DIALOG */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">¿Eliminar email?</Typography>
          <Typography sx={{ color: "#777", mt: 1 }}>
            {folder === "trash"
              ? "Esta acción elimina el correo definitivamente."
              : "El correo se moverá a la papelera."}
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={borrar}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ACCOUNTS DRAWER */}
      <Dialog
        open={openAccounts}
        onClose={() => {
          setOpenAccounts(false);
          reloadAccounts();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 760,
            bgcolor: "background.default",
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        <AccountManager onAccountsChange={reloadAccounts} />
      </Dialog>

      <AttachmentsLibrary
        open={attachmentsLibraryOpen}
        onClose={() => setAttachmentsLibraryOpen(false)}
        accounts={accounts}
        folder={folder}
        onOpenInConversation={openAttachmentInConversation}
      />

      {/* DELETE SELECTED DIALOG */}
      <Dialog
        open={showDeleteSelectedDialog}
        onClose={() => setShowDeleteSelectedDialog(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">¿Eliminar emails seleccionados?</Typography>

          <Typography sx={{ color: "#777", mt: 1 }}>
            {folder === "trash"
              ? "Los correos seleccionados se eliminarán definitivamente."
              : "Los correos seleccionados se moverán a la papelera."}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button onClick={() => setShowDeleteSelectedDialog(false)}>
              Cancelar
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteSelected}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
